import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { useMonthSnapshot } from '../hooks/useMonthSnapshot.js';
import { formatCurrency } from '../lib/format.js';

const monthFmt = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });

function formatMonthLabel(monthStart) {
  const d = new Date(monthStart + 'T00:00:00');
  const label = monthFmt.format(d);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Barre de progression : combien on a consommé/généré du forecast.
 * fill > 100% = dépassement (couleur d'alerte).
 */
function ProgressBar({ realised, forecast, baseColor, overColor }) {
  const safeForecast = forecast > 0 ? forecast : 0;
  const ratio = safeForecast > 0 ? realised / safeForecast : 0;
  const overrun = ratio > 1;
  const widthPct = Math.min(ratio, 1) * 100;
  const fillColor = overrun ? overColor : baseColor;
  return (
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${fillColor}`} style={{ width: `${widthPct}%` }} />
    </div>
  );
}

function pctLabel(realised, forecast) {
  if (!forecast || forecast <= 0) return '—';
  const pct = Math.round((realised / forecast) * 100);
  return `${pct}%`;
}

export default function MonthBreakEvenWidget({ month }) {
  const { data, isLoading, isError, refetch } = useMonthSnapshot(month);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4">
        <div className="h-36 bg-slate-100 rounded animate-pulse" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 flex flex-col items-center gap-3">
        <p className="text-sm text-slate-500">Impossible de calculer le point mort</p>
        <button
          onClick={() => refetch()}
          className="text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900"
          aria-label="Réessayer le chargement du point mort"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const chargesPct  = pctLabel(data.charges_realised_cents,  data.charges_forecast_cents);
  const revenusPct  = pctLabel(data.revenues_received_cents, data.revenues_forecast_cents);

  const net = data.net_projected_cents;
  const isPositive = net >= 0;
  const NetIcon = isPositive ? TrendingUp : net < -data.charges_forecast_cents * 0.1 ? AlertTriangle : TrendingDown;
  const netColorClass = isPositive ? 'text-emerald-700' : 'text-red-700';
  const netSign = isPositive ? '+' : '';

  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 space-y-3">
      <p className="text-sm font-semibold text-slate-800">
        {formatMonthLabel(data.month_start)} — Point mort
      </p>

      <div className="grid grid-cols-2 gap-3">
        {/* Charges */}
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500 font-medium">Charges</p>
          <div className="space-y-0.5">
            <div className="flex items-center justify-between gap-1">
              <span className="text-xs text-slate-500">Prévisionnel</span>
              <span className="text-xs font-medium text-red-600 tabular-nums">
                -{formatCurrency(data.charges_forecast_cents)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span className="text-xs text-slate-500">Réalisé</span>
              <span className="text-xs font-medium text-red-400 tabular-nums">
                -{formatCurrency(data.charges_realised_cents)}
              </span>
            </div>
          </div>
          <ProgressBar
            realised={data.charges_realised_cents}
            forecast={data.charges_forecast_cents}
            baseColor="bg-red-500"
            overColor="bg-red-700"
          />
          <p className="text-[11px] text-slate-500 tabular-nums">{chargesPct} consommé</p>
        </div>

        {/* Revenus */}
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500 font-medium">Revenus</p>
          <div className="space-y-0.5">
            <div className="flex items-center justify-between gap-1">
              <span className="text-xs text-slate-500">Prévisionnel</span>
              <span className="text-xs font-medium text-emerald-600 tabular-nums">
                +{formatCurrency(data.revenues_forecast_cents)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span className="text-xs text-slate-500">Généré</span>
              <span className="text-xs font-medium text-emerald-400 tabular-nums">
                +{formatCurrency(data.revenues_received_cents)}
              </span>
            </div>
          </div>
          <ProgressBar
            realised={data.revenues_received_cents}
            forecast={data.revenues_forecast_cents}
            baseColor="bg-emerald-500"
            overColor="bg-emerald-700"
          />
          <p className="text-[11px] text-slate-500 tabular-nums">{revenusPct} généré</p>
        </div>
      </div>

      {/* Footer marge nette */}
      <div className="bg-slate-50 rounded-lg px-3 py-2 flex items-center justify-center gap-2">
        <NetIcon size={14} className={netColorClass} aria-hidden />
        <p className={`text-xs font-semibold ${netColorClass} tabular-nums`}>
          Marge nette projetée : {netSign}{formatCurrency(net)}
        </p>
      </div>
    </div>
  );
}
